import React, { useMemo } from 'react';
import { Card, CardBody, Col, Container, Row, Spinner, Alert, Button } from 'reactstrap';
import Breadcrumbs from 'components/Common/Breadcrumb';
import TableContainer from 'components/Common/TableContainer';
import { Link, useNavigate } from 'react-router-dom';
import { ColumnDef } from '@tanstack/react-table';
import { SHOP_PATHS } from 'routes/routesConstants';
import { useShop } from 'context/shopContext';
import { useDeleteMutation, useListQuery } from 'http-client/hooks/useCrud';
import { API_ENDPOINTS } from 'http-client/httpConstants';
import { formatRelativeDate } from 'utils/dateFormatter';

interface ItemUnit {
  id: string;
  unitName: string;
  shopCategory: string;
  notes?: string;
  isActive: boolean;
  updatedAt: string;
}

const ItemUnitList: React.FC = () => {
  const { shop } = useShop();
  const { id: shopId, category } = shop!;

  const navigate = useNavigate();

  const {
    data: itemUnitData,
    isLoading,
    isError,
    error,
  } = useListQuery<ItemUnit>(API_ENDPOINTS.TENANT.ITEM_UNITS.BY_CATEGORY, {
    category: category || 'general',
  });

  const { mutate: deleteItemUnit } = useDeleteMutation<ItemUnit>(
    API_ENDPOINTS.TENANT.ITEM_UNITS.BASE,
    API_ENDPOINTS.TENANT.ITEM_UNITS.BY_CATEGORY,
  );

  const itemUnits: ItemUnit[] = useMemo(() => {
    return Array.isArray(itemUnitData) ? itemUnitData : [];
  }, [itemUnitData]);

  const handleEdit = (id: string) => {
    navigate(SHOP_PATHS.EDIT_UNIT.replace(':shopId', shopId || '').replace(':id', id));
  };

  // const onSuccess = () => {
  //   navigate(SHOP_PATHS.ITEM_UNITS.replace(':shopId', shopId || ''));
  // };

  // const onError = (error: any) => {
  //   handleFormErrors<CreateItemUnitFormValues>(error, formikHelpers);
  // };

  // if (isEditMode) {
  //   updateItemUnit({ data: values, params: { id: id } }, { onSuccess, onError });
  // }

  const handleDelete = (id: string) => {
    deleteItemUnit(id, {
      onSuccess: () => {
        // Optionally, show a success message or perform additional actions
      },
      onError: (error) => {
        alert(`Error deleting unit: ${(error as any)?.message || 'Unknown error'}`);
      },
    });
  };

  const columns: ColumnDef<ItemUnit>[] = useMemo(
    () => [
      {
        header: 'Unit Name',
        accessorKey: 'unitName',
        cell: ({ row }) => (
          <Link to={`/item-units/${row.original.id}`} className="text-dark fw-bold">
            {row.original.unitName}
          </Link>
        ),
      },
      {
        header: 'Category',
        accessorKey: 'shopCategory',
      },
      {
        header: 'Notes',
        accessorKey: 'notes',
        cell: ({ row }) => row.original.notes || '-',
      },
      {
        header: 'Actions',
        accessorKey: 'actions',
        cell: ({ row }) => (
          <div className="d-flex gap-2">
            <Button size="sm" color="primary" onClick={() => handleEdit(row.original.id)}>
              Edit
            </Button>
            <Button size="sm" color="danger" onClick={() => handleDelete(row.original.id)}>
              Delete
            </Button>
          </div>
        ),
      },
    ],
    [],
  );

  return (
    <div className="page-content">
      <Container fluid>
        <Breadcrumbs title="Units" breadcrumbItem="Item Units" />

        <Row>
          <Col lg="12">
            <Card>
              <CardBody>
                {isLoading ? (
                  <div className="text-center">
                    <Spinner color="primary" />
                  </div>
                ) : isError ? (
                  <Alert color="danger">{(error as any)?.message || 'Error loading units'}</Alert>
                ) : (
                  <TableContainer
                    columns={columns}
                    data={itemUnits}
                    isGlobalFilter={true}
                    isAddButton={true}
                    handleUserClick={() =>
                      navigate(`${SHOP_PATHS.ADD_UNIT.replace(':shopId', shopId || '')}`)
                    }
                    isPagination={true}
                    isCustomPageSize={true}
                    buttonClass="btn btn-success btn-rounded"
                    buttonName="Add Item Unit"
                    tableClass="align-middle table-nowrap dt-responsive nowrap w-100 table-borderless"
                    theadClass="table-light"
                    paginationWrapper="dataTables_paginate paging_simple_numbers pagination-rounded"
                    paginationClass="pagination"
                    SearchPlaceholder="Search item units..."
                  />
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default ItemUnitList;
